import { useState } from "react";

import { Button, InputAdornment, TextField, Typography } from "@mui/material";
import classNames from "classnames";
import { IoSync } from "react-icons/io5";

import { EWeightMeasurementUnit } from "../../../../../@types";
import { convertWeight, validators } from "../../../../../utils";
import { useMyCourseInstructor } from "../../hooks/useMyCourseInstructor";

export function MyCourseRequirement() {
	const { course, updateCourseBasicInformation } = useMyCourseInstructor();
	const [note, setNote] = useState(course?.note ?? "");
	const [loading, setLoading] = useState(false);

	let minWeightValue = "";
	if (course?.minWeight) {
		minWeightValue = convertWeight(
			Number(course.minWeight.split(" ")[0]),
			course.minWeight.split(" ")[1] as EWeightMeasurementUnit,
			EWeightMeasurementUnit.POUND,
		).toString();
	} else {
		minWeightValue = "";
	}

	let maxWeightValue = "";
	if (course?.maxWeight) {
		maxWeightValue = convertWeight(
			Number(course.maxWeight.split(" ")[0]),
			course.maxWeight.split(" ")[1] as EWeightMeasurementUnit,
			EWeightMeasurementUnit.POUND,
		).toString();
	} else {
		maxWeightValue = "";
	}

	const [minWeight, setMinWeight] = useState(minWeightValue);
	const [maxWeight, setMaxWeight] = useState(maxWeightValue);

	if (!course) {
		return null;
	}

	const onClickCancel = () => {
		setNote(course.note);
		setMinWeight(minWeightValue);
		setMaxWeight(maxWeightValue);
	};

	const isDisabled =
		(note.trim() === course.note.trim() &&
			minWeight === minWeightValue &&
			maxWeight === maxWeightValue) ||
		loading;
	const isEdited =
		(note !== course.note ||
			minWeight !== minWeightValue ||
			maxWeight !== maxWeightValue) &&
		!loading;

	const onClickUpdate = async () => {
		if (isDisabled) {
			return;
		}

		let newMinWeight = "";
		if (minWeight) {
			newMinWeight = `${minWeight} ${EWeightMeasurementUnit.POUND}`;
		}

		let newMaxWeight = "";
		if (maxWeight) {
			newMaxWeight = `${maxWeight} ${EWeightMeasurementUnit.POUND}`;
		}

		setLoading(true);
		await updateCourseBasicInformation({
			note: note.trim(),
			minWeight: newMinWeight,
			maxWeight: newMaxWeight,
		});
		setLoading(false);
	};

	return (
		<>
			<div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
				<Typography className="small-title-text bold">
					5. Focus on students who are a good fit. (Optional)
				</Typography>
				{loading && (
					<div>
						<IoSync className="normal-icon rotation-icon blue-icon" />
					</div>
				)}
			</div>
			<Typography className="normal-text">
				Inform students whether they need test results, certificates, ratings,
				endorsements, experience, a minimum age, height, etc:
			</Typography>
			<div className="common-input item-separator-small">
				<TextField
					size="small"
					autoComplete="off"
					id="course-note"
					onChange={(e) => {
						setNote(e.target.value);
					}}
					value={note}
					fullWidth
					multiline
					minRows={4}
					placeholder="Save yourself some energy and identify the potential roadblocks upfront"
					slotProps={{
						input: {
							className: "normal-text",
							classes: {
								input: "input-placeholder",
							},
						},
					}}
				/>
			</div>
			<Typography className="normal-text item-separator-big">
				If you are using an aircraft you own or control, share the allowable
				weight range for carrying a student:
			</Typography>
			<div className="course-weight-container item-separator-small">
				<div className="common-input" style={{ width: "unset" }}>
					<TextField
						size="small"
						autoComplete="off"
						id="course-min-weight"
						onChange={(e) => {
							if (
								e.target.value !== "" &&
								!validators.weight.test(e.target.value)
							) {
								return;
							}

							setMinWeight(e.target.value);
						}}
						value={minWeight}
						slotProps={{
							input: {
								className: "normal-text",
								classes: {
									input: "input-placeholder",
								},
								inputMode: "numeric",
								endAdornment: (
									<InputAdornment position="end">
										<Typography className="normal-text">lbs</Typography>
									</InputAdornment>
								),
							},
						}}
					/>
				</div>
				<Typography className="normal-text">-</Typography>
				<div className="common-input" style={{ width: "unset" }}>
					<TextField
						size="small"
						autoComplete="off"
						id="course-max-weight"
						onChange={(e) => {
							if (
								e.target.value !== "" &&
								!validators.weight.test(e.target.value)
							) {
								return;
							}

							setMaxWeight(e.target.value);
						}}
						value={maxWeight}
						slotProps={{
							input: {
								className: "normal-text",
								classes: {
									input: "input-placeholder",
								},
								inputMode: "numeric",
								endAdornment: (
									<InputAdornment position="end">
										<Typography className="normal-text">lbs</Typography>
									</InputAdornment>
								),
							},
						}}
					/>
				</div>
			</div>
			<div className="button-container item-separator-small">
				<Button
					className="normal-button disabled-button"
					disabled={!isEdited}
					onClick={onClickCancel}>
					Cancel
				</Button>
				<Button
					disabled={isDisabled}
					className={classNames("normal-button", {
						"primary-button": !isDisabled,
						"disabled-button": isDisabled,
					})}
					onClick={onClickUpdate}>
					Save
				</Button>
			</div>
		</>
	);
}
